/**
 * useCalendarAvailability Hook
 * 
 * Purpose:
 * Custom hook for fetching calendar availability data (Hostaway overlays) for a specific month.
 * Fetches availability status for each day in the month to display availability overlays on the calendar.
 * 
 * High-level Workflow:
 * 1. Guard checks: Validates prerequisites (listingId, propertiesLoading, selectedProperty)
 *    before attempting fetch. If prerequisites not met, clears data and returns.
 * 2. Date range calculation: Uses exact month bounds (monthStart to monthEnd) for availability
 *    fetch (no wide range needed like reservations).
 * 3. API fetch: Calls getCalendarAvailability with listingId and date range.
 * 4. Response processing: Extracts days map from response (handles wrapped/flat formats).
 * 5. Error handling: Shows user-facing error message on failure.
 * 6. Cleanup: Prepares for future AbortController support (currently not supported by API).
 * 
 * Assumptions & Invariants:
 * - Fetch gating: All fetches gated by listingId exists, propertiesLoading is false, and
 *   selectedProperty object exists. Same guards as reservations fetch.
 * - Date range: Uses exact month bounds (no widening needed). Availability is day-by-day
 *   status, so we only need the exact month range.
 * - Response format: Response contains a "days" object/map with day-by-day availability status.
 *   Handles both wrapped ({ data: { days: {...} } }) and flat ({ days: {...} }) formats.
 * - Error handling: Simple error handling - shows error message on failure, clears data.
 *   No special 401/403 handling (availability fetch doesn't have auth-not-ready issues).
 * - AbortController: Currently not supported by getCalendarAvailability API, but cleanup
 *   function is prepared for future support.
 * 
 * Dependencies:
 * - Internal:
 *   - services/ownerApi.js: getCalendarAvailability API function
 * - External:
 *   - react: useState, useEffect
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 2 refactoring)
 */

import { useState, useEffect } from 'react';
import { getCalendarAvailability } from '../services/ownerApi.js';

/**
 * Hook for fetching calendar availability (Hostaway overlays)
 * 
 * @param {number|null} listingId - Property listing ID (primitive, stable)
 * @param {Date} monthStart - Start of month (Date object)
 * @param {Date} monthEnd - End of month (Date object)
 * @param {boolean} propertiesLoading - Whether properties are still loading
 * @param {Object|null} selectedProperty - Selected property object (for guard check)
 * @returns {Object} { availability, availabilityLoading, availabilityError }
 */
export function useCalendarAvailability({
  listingId,
  monthStart,
  monthEnd,
  propertiesLoading,
  selectedProperty,
}) {
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);

  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  useEffect(() => {
    if (!listingId || propertiesLoading || !selectedProperty) {
      setAvailability(null);
      setAvailabilityLoading(false);
      setAvailabilityError(null);
      return;
    }

    const monthStartDate = new Date(monthStart);
    monthStartDate.setHours(0, 0, 0, 0);
    const monthEndDate = new Date(monthEnd);
    monthEndDate.setHours(23, 59, 59, 999);
    const startDate = monthStartDate.toISOString().split('T')[0];
    const endDate = monthEndDate.toISOString().split('T')[0];

    if (isNaN(monthStartDate.getTime()) || isNaN(monthEndDate.getTime()) || monthStartDate >= monthEndDate) {
      setAvailabilityError('Invalid month bounds');
      setAvailability(null);
      setAvailabilityLoading(false);
      return;
    }

    if (debug) {
      console.log('[CalendarPage] DEBUG availability fetch REQUEST', {
        requestedRange: { start: startDate, end: endDate },
        listingId,
        timestamp: new Date().toISOString(),
      });
    }

    setAvailabilityLoading(true);
    setAvailabilityError(null);

    const fetchAvailability = async () => {
      try {
        const response = await getCalendarAvailability({
          listingId: listingId,
          start: startDate,
          end: endDate,
        });
        const daysMap = response.days || response.data?.days || {};
        const keys = Object.keys(daysMap).filter((k) => /^\d{4}-\d{2}-\d{2}$/.test(k));
        const minKey = keys.length ? keys.reduce((a, b) => (a < b ? a : b)) : null;
        const maxKey = keys.length ? keys.reduce((a, b) => (a > b ? a : b)) : null;

        if (debug) {
          console.log('[CalendarPage] DEBUG availability fetch RESPONSE', {
            requestedRange: { start: startDate, end: endDate },
            responseCoverage: { minKey, maxKey, dayKeysCount: keys.length },
            listingId,
            timestamp: new Date().toISOString(),
          });
        }

        setAvailability(daysMap);
        setAvailabilityError(null);
      } catch (err) {
        console.error('[CalendarPage] Error fetching availability:', err);
        setAvailabilityError(err.message || 'Failed to fetch availability');
        setAvailability(null);
      } finally {
        setAvailabilityLoading(false);
      }
    };

    fetchAvailability();
    return () => {};
  }, [listingId, monthStart, monthEnd, propertiesLoading, selectedProperty]);

  return {
    availability,
    availabilityLoading,
    availabilityError,
  };
}
